import React, { useState } from "react";
import { useAuth } from "@/lib/auth";
import { Btn, Input } from "@/components/UI";
import axios from "axios";
import { ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Login() {
  const { login } = useAuth();
  const [username, setUsername] = useState("admin");
  const [password, setPassword] = useState("admin123");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  // iter224 — MFA challenge stage
  const [challengeToken, setChallengeToken] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [mfaMode, setMfaMode] = useState(false);

  const finishLogin = async (data) => {
    // Same permissions fetch as useAuth().login does
    let perms = {}, actions = {};
    try {
      const p = await axios.get(`${API}/me/permissions`, { headers: { Authorization: `Bearer ${data.token}` } });
      perms = p.data.modules || {};
      actions = p.data.actions || {};
    } catch { /* ignore */ }
    const userWithPerms = { ...data.user, module_permissions: perms, action_permissions: actions };
    localStorage.setItem("erp_token", data.token);
    localStorage.setItem("erp_user", JSON.stringify(userWithPerms));
    const redirect = localStorage.getItem("post_login_redirect");
    if (redirect) { localStorage.removeItem("post_login_redirect"); window.location.href = redirect; }
    else { window.location.href = "/"; }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const isEmail = username.includes("@");
      const body = isEmail ? { email: username, password } : { username, password };
      const { data } = await axios.post(`${API}/auth/login`, body);
      if (data.mfa_required) {
        setChallengeToken(data.challenge_token);
        setMfaMode(true);
      } else {
        await finishLogin(data);
      }
    } catch (e) {
      setErr(e.response?.data?.detail || "Giriş başarısız");
    } finally { setLoading(false); }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/mfa/verify`, {
        challenge_token: challengeToken,
        code: mfaCode.trim(),
      });
      await finishLogin(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Doğrulama başarısız");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      <div className="max-w-md w-full">
        <div className="flex flex-col items-center gap-3 mb-8">
          <img src="/logo.png" alt="Meta Pharma ERP" data-testid="brand-logo" className="h-32 w-auto object-contain" style={{ maxWidth: "220px" }} />
          <div className="text-center">
            <div className="text-lg font-bold tracking-tight" style={{ fontFamily: "'Manrope', sans-serif" }}>Meta Pharma ERP</div>
            <div className="text-xs text-slate-500 uppercase tracking-wider">Fason Üretim Yönetimi</div>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
          {!mfaMode ? (
            <>
              <h1 className="text-xl font-semibold mb-1" style={{ fontFamily: "'Manrope', sans-serif" }}>Sisteme Giriş</h1>
              <p className="text-sm text-slate-500 mb-5">Kullanıcı adı ve şifrenizle oturum açın</p>
              <form onSubmit={submit} className="space-y-3">
                <Input label="Kullanıcı Adı" type="text" autoComplete="username" value={username} onChange={(e) => setUsername(e.target.value)} data-testid="login-username" required />
                <Input label="Şifre" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" required />
                {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}
                <Btn type="submit" disabled={loading} data-testid="login-submit" className="w-full justify-center">
                  {loading ? "Giriş yapılıyor..." : "Giriş Yap"}
                </Btn>
              </form>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck size={22} className="text-emerald-600"/>
                <h1 className="text-xl font-semibold" style={{ fontFamily: "'Manrope', sans-serif" }}>İki Aşamalı Doğrulama</h1>
              </div>
              <p className="text-sm text-slate-500 mb-5">
                Doğrulayıcı uygulamanızdaki 6 haneli kodu veya bir kurtarma kodunu girin.
              </p>
              <form onSubmit={submitMfa} className="space-y-3">
                <Input
                  label="TOTP / Kurtarma kodu"
                  type="text"
                  autoComplete="one-time-code"
                  inputMode="text"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.toUpperCase())}
                  data-testid="login-mfa-code"
                  required
                />
                {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}
                <Btn type="submit" disabled={loading || mfaCode.length < 6} data-testid="login-mfa-submit" className="w-full justify-center">
                  {loading ? "Doğrulanıyor..." : "Doğrula ve Giriş Yap"}
                </Btn>
                <button type="button" onClick={() => { setMfaMode(false); setChallengeToken(""); setMfaCode(""); setErr(""); }}
                        className="w-full text-xs text-slate-500 hover:text-slate-700 pt-1" data-testid="login-mfa-cancel">
                  ← Şifre girişine dön
                </button>
              </form>
            </>
          )}
          <div className="mt-6 text-xs text-slate-500 border-t border-slate-100 pt-4">
            <div className="font-semibold text-slate-700 mb-1">Rol tipleri:</div>
            <div>• <b>Sistem Yöneticisi</b> — Tüm modüllere ve işlemlere yetkili</div>
            <div>• <b>Müdür</b> — Kendisine verilen yetkiler dahilinde işlem yapar</div>
            <div>• <b>Kullanıcı</b> — Yalnız izin verilen sayfalarda; düzenleme ve silme yapamaz</div>
          </div>
        </div>
      </div>
    </div>
  );
}
